import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { isNativeApp, getRememberMePreference } from '@/lib/authStorage';

const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const WARNING_TIME = 4 * 60 * 1000; // 4 minutes (1 minute before logout)

export const useInactivityTimeout = () => {
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasShownWarning = useRef(false);

  const shouldEnableTimeout = useCallback((): boolean => {
    // Don't enable on mobile
    if (isNativeApp()) return false;
    
    // Don't enable if "Remember Me" was checked
    if (getRememberMePreference()) return false;
    
    // Only enable if user is logged in
    return !!user;
  }, [user]);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
  }, []);

  const handleInactivityLogout = useCallback(async () => {
    clearTimers();
    toast({
      title: 'Session Expired',
      description: 'You have been logged out due to inactivity.',
      variant: 'default',
    });
    await signOut();
  }, [clearTimers, toast, signOut]);

  const showWarning = useCallback(() => {
    if (!hasShownWarning.current) {
      hasShownWarning.current = true;
      toast({
        title: 'Session Expiring',
        description: 'You will be logged out in 1 minute due to inactivity. Move your mouse or press a key to stay logged in.',
        duration: 30000, // Show for 30 seconds
      });
    }
  }, [toast]);

  const resetTimer = useCallback(() => {
    if (!shouldEnableTimeout()) return;

    clearTimers();
    hasShownWarning.current = false;

    // Set warning timer (4 minutes)
    warningRef.current = setTimeout(showWarning, WARNING_TIME);

    // Set logout timer (5 minutes)
    timeoutRef.current = setTimeout(handleInactivityLogout, INACTIVITY_TIMEOUT);
  }, [shouldEnableTimeout, clearTimers, showWarning, handleInactivityLogout]);

  useEffect(() => {
    if (!shouldEnableTimeout()) {
      clearTimers();
      return;
    }

    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'touchstart',
      'scroll',
      'click',
    ];

    // Throttle the reset to avoid excessive timer resets
    let lastActivity = Date.now();
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastActivity > 1000) { // Only reset if more than 1 second since last reset
        lastActivity = now;
        resetTimer();
      }
    };

    // Add event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, throttledReset, { passive: true });
    });

    // Start the initial timer
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, throttledReset);
      });
      clearTimers();
    };
  }, [shouldEnableTimeout, resetTimer, clearTimers]);

  return { resetTimer };
};
